/**
 * World Modifier system.
 *
 * Every 4 boss kills the player is offered 3 modifier combinations to choose from.
 * Each combination applies debuffs (harder challenge) in exchange for permanent bonuses
 * (more XP, more gold, better item drop chance) for the rest of the run.
 *
 * Active modifiers REPLACE the previous one — debuffs do not stack across choices.
 */

// ── Individual debuffs ───────────────────────────────────────────────────────

/**
 * Full pool of 12 debuffs.
 * `effects` keys match the fields on the modifier object read by the game loop.
 * Multiplicative keys (see MULT_KEYS) are combined with ×; all others with +.
 */
export const DEBUFFS = [
  { id: 'player_slow',    label: '🐌 Sluggish',        desc: 'Player move speed −20%',        effects: { playerSpeedMult:  0.80 } },
  { id: 'attack_slow',    label: '⏳ Heavy Limbs',      desc: 'Attack cooldown +30%',          effects: { playerCdMult:     1.30 } },
  { id: 'range_down',     label: '🎯 Short Reach',      desc: 'Attack range −20%',             effects: { playerRangeMult:  0.80 } },
  { id: 'damage_down',    label: '🩹 Weakened',         desc: 'Attack damage −2',              effects: { playerDmgFlatMod: -2   } },
  { id: 'enemy_fast',     label: '💨 Frenzied Horde',   desc: 'Enemy speed +25%',              effects: { enemySpeedMult:   1.25 } },
  { id: 'enemy_tough',    label: '💪 Ironclad',         desc: 'Enemy HP +30%',                 effects: { enemyHpMult:      1.30 } },
  { id: 'enemy_armored',  label: '🛡️ Iron Skin',         desc: 'Enemy armor +4',                effects: { enemyArmorFlat:   4    } },
  { id: 'more_elites',    label: '👑 Elite Surge',       desc: 'Elite spawn chance +20%',       effects: { eliteChanceBonus: 0.20 } },
  { id: 'fire_vuln',      label: '🔥 Scorched',         desc: 'Fire resistance −30%',          effects: { fireResDown:      0.30 } },
  { id: 'lightning_vuln', label: '⚡ Exposed',          desc: 'Lightning resistance −30%',     effects: { lightningResDown: 0.30 } },
  { id: 'enemy_crit',     label: '🗡️ Battle Fury',       desc: 'Enemy crit rate +10%',          effects: { enemyCritBonus:   0.10 } },
  { id: 'enemy_damage',   label: '💢 Savage Horde',     desc: 'Enemy base damage +30%',        effects: { enemyDamageMult:  1.30 } },
];

// ── Rarity configuration ─────────────────────────────────────────────────────

/**
 * Debuff count and bonus multipliers for each rarity tier.
 * Higher rarity = more debuffs stacked + better rewards.
 */
export const MODIFIER_RARITY_CONFIG = {
  common:    { debuffCount: 1, xpMult: 1.15, goldMult: 1.15, dropChanceBonus: 0.002, label: 'Common',    color: '#9e9e9e' },
  uncommon:  { debuffCount: 2, xpMult: 1.30, goldMult: 1.30, dropChanceBonus: 0.005, label: 'Uncommon',  color: '#4caf50' },
  rare:      { debuffCount: 3, xpMult: 1.50, goldMult: 1.50, dropChanceBonus: 0.010, label: 'Rare',      color: '#2196f3' },
  epic:      { debuffCount: 4, xpMult: 1.75, goldMult: 1.75, dropChanceBonus: 0.020, label: 'Epic',      color: '#9c27b0' },
  legendary: { debuffCount: 5, xpMult: 2.20, goldMult: 2.20, dropChanceBonus: 0.050, label: 'Legendary', color: '#ff9800' },
};

/** Weighted probability for selecting each rarity as a presented choice. */
const MODIFIER_RARITY_WEIGHTS = { common: 40, uncommon: 30, rare: 20, epic: 8, legendary: 2 };

/** Effects whose values multiply together when stacking debuffs (vs. adding). */
const MULT_KEYS = new Set([
  'playerSpeedMult', 'playerCdMult', 'playerRangeMult',
  'enemySpeedMult',  'enemyHpMult',  'enemyDamageMult',
]);

// ── Generator functions ──────────────────────────────────────────────────────

/**
 * Build one modifier combination for the given rarity.
 * Randomly selects `debuffCount` debuffs, stacks their effects, and attaches
 * the rarity bonus multipliers (xpMult, goldMult, dropChanceBonus).
 *
 * The returned object is stored in `s.activeModifier` and read by the game loop.
 */
export function generateModifierCombination(rarity) {
  const config = MODIFIER_RARITY_CONFIG[rarity];
  const chosen = [...DEBUFFS].sort(() => Math.random() - 0.5).slice(0, config.debuffCount);

  // Start from neutral base effects + rarity bonus rewards
  const effects = {
    playerSpeedMult:  1,
    playerCdMult:     1,
    playerRangeMult:  1,
    playerDmgFlatMod: 0,
    enemySpeedMult:   1,
    enemyHpMult:      1,
    enemyArmorFlat:   0,
    eliteChanceBonus: 0,
    fireResDown:      0,
    lightningResDown: 0,
    enemyCritBonus:   0,
    enemyDamageMult:  1,
    xpMult:           config.xpMult,
    goldMult:         config.goldMult,
    dropChanceBonus:  config.dropChanceBonus,
  };

  for (const debuff of chosen) {
    for (const [key, val] of Object.entries(debuff.effects)) {
      effects[key] = MULT_KEYS.has(key) ? effects[key] * val : effects[key] + val;
    }
  }

  return {
    rarity,
    label:   config.label,
    color:   config.color,
    debuffs: chosen,
    ...effects,
  };
}

/**
 * Generate `count` modifier combinations, each with a distinct rarity.
 * Rarities are sampled without replacement using MODIFIER_RARITY_WEIGHTS.
 * Result is sorted ascending by rarity weight (easiest → hardest).
 */
export function generateModifierCombinations(count = 3) {
  const remaining = Object.keys(MODIFIER_RARITY_WEIGHTS);
  const pickedRarities = [];

  for (let i = 0; i < Math.min(count, remaining.length); i++) {
    const totalW = remaining.reduce((s, r) => s + MODIFIER_RARITY_WEIGHTS[r], 0);
    let rnd = Math.random() * totalW;
    let chosen = remaining[remaining.length - 1]; // fallback
    for (const r of remaining) {
      rnd -= MODIFIER_RARITY_WEIGHTS[r];
      if (rnd <= 0) { chosen = r; break; }
    }
    pickedRarities.push(chosen);
    remaining.splice(remaining.indexOf(chosen), 1);
  }

  // Sort so the card order is always: lighter rarity first → heavier rarity last
  const ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
  pickedRarities.sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));

  return pickedRarities.map(generateModifierCombination);
}
